import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';
import { squidloansAuthAPI, squidloansUserAPI } from '../../services/api';
import { useAuth } from '../../Context/AuthContext';
import { leadStageToRouteMap } from '../../Utils/constants';
import FlowFooter from '../../components/FlowFooter';

function OTPMobile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMobile, setErrorMobile] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60);
  const [isContinuingJourney, setIsContinuingJourney] = useState(false);
  const inputRef = useRef(null);

  const { PAN, phone_number, ...utmParams } =
    location.state || JSON.parse(sessionStorage.getItem('panMobileData') || '{}');

  useEffect(() => {
    if (!PAN || !phone_number) {
      navigate('/apply/pan-mobile', { replace: true });
    }
  }, [PAN, phone_number, navigate]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOtp(value);
      setErrorMobile('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setErrorMobile('Please enter the OTP');
      return;
    }
    if (otp.length < 4) {
      setErrorMobile('OTP must be at least 4 digits');
      return;
    }

    setIsLoading(true);
    setErrorMobile('');
    try {
      const { clickid, ...restUtm } = utmParams;
      const data = { PAN, otp, phone_number, ...restUtm, click_id: clickid || utmParams.click_id || '' };
      const response = await squidloansAuthAPI.verifyOTP(data);
      if (response?.token) {
        login(response.token);
        // Fire AdsPlay affiliate install tracking after OTP verification (if clickid present)
        const clickidForPixel = utmParams.clickid || utmParams.click_id || '';
        if (clickidForPixel) {
          const adsplayPixel = document.createElement('img');
          adsplayPixel.src = `https://affiliates.adsplay.in/trackingcode_installs.php?clickid=${encodeURIComponent(clickidForPixel)}`;
          adsplayPixel.width = 1;
          adsplayPixel.height = 1;
          adsplayPixel.style.display = 'none';
          adsplayPixel.alt = '';
          document.body.appendChild(adsplayPixel);
        }
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'conversion', {
            send_to: 'AW-17895773186/aF0YCOOstbscEIKor9VC',
            value: 1.0,
            currency: 'INR',
          });
        }
        const journey = await squidloansUserAPI.getJourney();
        sessionStorage.removeItem('panMobileData');
        const stage = journey?.journey?.lead_stage;
        localStorage.setItem('userPhone', phone_number);
        if (stage && leadStageToRouteMap[stage]) {
          navigate(leadStageToRouteMap[stage]);
        } else {
          navigate('/apply-now');
        }
      }
    } catch (error) {
      setErrorMobile(error.message || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    try {
      setIsLoading(true);
      setOtp('');
      await squidloansAuthAPI.resendOTP({ PAN, phone_number });
      const stateData = { PAN, phone_number, ...utmParams };
      sessionStorage.setItem('panMobileData', JSON.stringify(stateData));
      setErrorMobile('');
      setResendCooldown(60);
    } catch (error) {
      setErrorMobile(error.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueJourney = async () => {
    try {
      setIsContinuingJourney(true);
      const journey = await squidloansUserAPI.getJourney();
      const stage = journey?.journey?.lead_stage;
      if (stage && leadStageToRouteMap[stage]) {
        navigate(leadStageToRouteMap[stage]);
      } else {
        navigate('/apply-now');
      }
    } catch (error) {
      setErrorMobile(error?.message || 'Unable to determine next step.');
    } finally {
      setIsContinuingJourney(false);
    }
  };

  if (!PAN || !phone_number) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-md w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              Verify your <span className="text-[#FF6B35]">mobile</span>
            </h1>
            <p className="text-sm sm:text-base text-[#64748B]">OTP sent to your phone number</p>
          </div>

          {isAuthenticated ? (
            <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-400 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl">
              <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
                <div className="bg-green-500 p-2 sm:p-3 rounded-full border-2 border-green-600">
                  <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-green-700 px-2">Your mobile number has been verified</h3>
                <p className="text-sm sm:text-base text-gray-600 px-2">Continue your loan application journey</p>
                <button
                  onClick={handleContinueJourney}
                  disabled={isContinuingJourney}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white rounded-xl text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-70 shadow-md hover:shadow-lg w-full max-w-xs"
                >
                  {isContinuingJourney ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    'Continue Journey'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="otp" className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                    Enter OTP <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    ref={inputRef}
                    type="text"
                    id="otp"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={otp}
                    onChange={handleChange}
                    placeholder="Enter 6 digit OTP"
                    maxLength={6}
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base text-center font-medium ${
                      errorMobile ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                    } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 transition-all`}
                    autoFocus
                  />
                  {errorMobile && <p className="text-xs text-red-500 mt-1 sm:mt-2">{errorMobile}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>

                <div className="text-center">
                  <p className="text-xs sm:text-sm text-[#64748B] mb-2">
                    Didn&apos;t receive the code?
                  </p>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={isLoading || resendCooldown > 0}
                    className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-xs sm:text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg ${
                      isLoading || resendCooldown > 0 ? '' : ''
                    }`}
                  >
                    {resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : 'Resend OTP'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      <FlowFooter />
    </div>
  );
}

export default OTPMobile;
